<template>
    <div>
        <div v-if="deal.objective" class="box">
            <div class="deal-form-title">{{ deal.influencer_title || deal.title }}</div>

            <div v-html="$sanitize(deal.objective)"></div>
        </div>

        <div class="box">
            <div class="deal-form-title">
                {{ customizationText('dates_header', 'Timeline & Schedule') }}
            </div>
            Our partnership is set to start on {{ deal.start_date_readable }} and will last for
            {{ deal.duration_length }} {{ deal.duration_interval }}s, ending on {{ deal.end_date_readable }}.
        </div>

        <div v-if="show_compensation" class="box">
            <div class="deal-form-title">
                {{ customizationText('compensation_step_title', 'Compensation') }}
            </div>

            <div>{{ customizationText('compensation_step_overview', '') }}</div>

            <!--
                You are set to receive the following compensation for this partnership.

                <div v-if="deal.deal_show_influencer_budget">
                <money-display :currency="deal.brand.currency" :value="(deal.budget / 100)" />
                </div>
                <div v-if="deal.deal_show_influencer_compensation">
                <div v-if="hasCompensation('money')">Payments</div>
                <div v-if="hasCompensation('product')">Product</div>
                <div v-if="hasCompensation('affiliate')">Commission</div>
                </div>
            -->
        </div>

        <div class="box">
            <div class="deal-form-title">
                {{ customizationText('tasks_step_title', 'Deliverables') }}
            </div>

            <div class="mt-15">
                <deliverable-preview
                    v-for="deliverable in deal.deliverables"
                    :key="deliverable.id"
                    :deal="deal"
                    :deliverable="deliverable"
                />
            </div>
        </div>

        <div v-if="assets.length" class="box">
            <div class="campaign-form-title">
                {{ customizationText('assets_step_title', 'Assets') }}
            </div>

            <p>{{ customizationText('assets_step_overview', ' Please take a look at the following assets:') }}</p>

            <div class="mt-15 mb-15">
                <div v-for="asset in assets">
                    <a :href="asset.url" target="_blank">{{ asset.name }}</a>
                </div>
            </div>
        </div>

        <div v-if="deal.signature_request_id || contract_asset" class="box">
            <div class="deal-form-title">
                {{ customizationText('contract_header', 'Agreement & Contract') }}
            </div>

            Please review the contract agreement below:

            <div v-if="deal.signature_request_id" class="mt-15">
                <signature-request :campaign="deal" />
            </div>

            <div v-else-if="contract_extension" class="mt-15">
                <iframe
                    v-if="contract_extension === 'doc'"
                    class="contract-embed"
                    :src="`https://view.officeapps.live.com/op/embed.aspx?src=${contract_asset.url}`"
                    frameborder="0"
                >
                </iframe>
                <div v-else class="contract-embed mt-15"></div>
            </div>
        </div>

        <div v-if="deal.content_rights" class="box">
            <div class="deal-form-title">Content Rights</div>
            <div class="mt-15">
                <content-rights-preview :content-rights="deal.content_rights.content_rights" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .contract-embed {
        background-color: #eee;
        padding: 10px;
        width: 100%;
        height: 600px;
    }
</style>

<script>
    import DeliverablePreview from './preview/DeliverablePreview';
    import MoneyDisplay from '../../../interface/elements/MoneyDisplay';
    import CampaignCustomizeMixin from '../../../../mixins/CampaignCustomizeMixin';
    import SignatureRequest from '../../edocument/SignatureRequest';
    import ContentRightsPreview from '../../campaign/proposal/ContentRightsPreview';
    import PDFObject from '../../../../../vendor/pdfobject';

    export default {
        /**
         * The component name
         *
         * @type {string}
         */
        name: 'DealPreview',

        /**
         * The component properties
         *
         * @type {Object}
         */
        props: {
            /**
             * The deal
             */
            deal: {
                type: Object,
                required: true
            },

            /**
             * The deal jobs
             */
            jobs: {
                type: Array,
                required: true
            }
        },

        /**
         * The component mixins
         *
         * @type {array}
         */
        mixins: [CampaignCustomizeMixin],

        /**
         * The child components
         *
         * @type {object}
         */
        components: {
            SignatureRequest,
            MoneyDisplay,
            DeliverablePreview,
            ContentRightsPreview
        },

        /**
         * The component data
         *
         * @returns {object}
         */
        data() {
            return {};
        },

        /**
         * The component's computed properties
         *
         * @type {object}
         */
        computed: {
            /**
             * The campaign for customization
             *
             * @returns {object}
             */
            campaign() {
                return this.deal;
            },

            /**
             * Whether to show the compensation
             *
             * @returns {boolean}
             */
            show_compensation() {
                return true;
            },

            /**
             * The deal assets
             *
             * @returns {Array}
             */
            assets() {
                return this.deal.assets.filter((asset) => asset.type !== 'contract');
            },

            /**
             * The contract asset
             *
             * @return {object}
             */
            contract_asset() {
                const contracts = this.deal.assets.filter((asset) => asset.type === 'contract');

                return contracts.length ? contracts[0] : null;
            },

            /**
             * The contract extension
             *
             * @returns {string|null}
             */
            contract_extension() {
                if (!this.contract_asset) {
                    return null;
                }

                if (this.contract_asset.mime) {
                    if (this.contract_asset.mime.indexOf('pdf') >= 0) {
                        return 'pdf';
                    }

                    return 'doc';
                }

                const parts = this.contract_asset.url.split('.');
                return parts.length > 1 ? parts[1] : null;
            }
        },

        /**
         * The component's computed properties
         *
         * @type {object}
         */
        methods: {
            /**
             * Whether the deal has the given compensation method
             *
             * @param   {string} method
             * @returns {boolean}
             */
            hasCompensation(method) {
                return this.deal.compensation.indexOf(method) >= 0;
            },

            /**
             * Load the embedded contract
             */
            loadContract() {
                if (!this.contract_asset || this.deal.signature_request_id || this.contract_extension !== 'pdf') {
                    return;
                }

                const options = {
                    pdfOpenParams: { scrollbar: '0', toolbar: '0', statusbar: '0', messages: '0', navpanes: '0' },
                    fallbackLink:
                        '<p>This browser does not support inline PDFs. Please download the ' +
                        'contract to view it: <a href="[url]">Download Contract</a></p>'
                };

                PDFObject.embed(this.contract_asset.url, '.contract-embed', options);
            }
        },

        /**
         * When the component is first created
         */
        mounted() {
            this.loadContract();
        }
    };
</script>
